import * as React from "react";
import MimoLogo from "./mimoLogo";
import { signOut, selector as signOutApi } from "./sidebarSlice";
import { Sidebar, Menu, MenuItem } from "react-pro-sidebar";
import {
  MenuIconDashboard,
  MenuIconDeviceManagement,
  MenuIconAlerts,
  MenuIconIntegrations,
  MenuIconDeviceSetUp,
  MenuIconUsers,
  HamburgerIcon,
  LogoutIcon,
  MenuManageFirmwareIcon,
  MenuQuickAlertIcon,
  ChangePasswordIcon,
  HelpIcon,
  MenuIconAnalytics,
} from "../../../components/ui/icons";
import ChangePassword from "../../../components/modals/changePassword";
import { Button, Modal, Form, InputGroup } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { routes, Status } from "../../../common/constants";
import { roleIds } from "../../../config";
import { useDispatch, useSelector } from "react-redux";
import { userRoles } from "../../../routes/util";
import { deleteCookies } from "../../../common/utils";
import Spinner from "../../../components/ui/spinner";

const SidebarComp = () => {
  const dispatch: any = useDispatch();
  const signOutResponse: any = useSelector(signOutApi);
  const [collapsed, setCollapsed] = React.useState<boolean>(false);
  const [changePasswordVisible, setChangePasswordVisible] =
    React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [broken, setBroken] = React.useState(
    window.matchMedia("(max-width: 800px)").matches
  );

  const location = useLocation();
  const userEmail = useSelector((state: any) => state.users.email);
  const isPasswordEnable = useSelector(
    (state: any) => state.users?.isPasswordEnable ?? false
  );
  const userRole: string = useSelector((state: any) => state.users.roleName);
  const role = userRoles.filter((user) => user.id == userRole)[0]?.displayName;
  let homePageUrl: string;
  if (
    userRole == roleIds.admin ||
    userRole == roleIds.operator ||
    userRole == roleIds.viewer
  )
    homePageUrl = routes.dashboard;
  else if (userRole == roleIds.superAdmin) homePageUrl = routes.superAdmin;

  React.useEffect(() => {
    if (signOutResponse.status === Status.PENDING) {
      setIsLoading(true);
    }
    else if (signOutResponse.status === Status.SUCCESS) {
      deleteCookies();
      window.location.href = "/";
    } else if (signOutResponse.status === Status.FAILED) {
      window.location.href = "/dashboard";
    }
  }, [signOutResponse.status, dispatch]);

  const logout: () => void = React.useCallback(
    async function () {
      dispatch(signOut());
    },
    [dispatch]
  );

  const handleHamburgerClick = (e) => {
    e.preventDefault();
    setCollapsed(!collapsed);
  };

  const alertsRoutes = () => {
    const path = location.pathname;
    return path.includes(routes.alerts) && !path.includes(routes.quickAlerts);
  };

  const changePassword = (): void => {
    setChangePasswordVisible((prev) => !prev);
  };

  return (
    <>
      <Spinner isActive={isLoading} />
      <div className="sidebar_content">
        {changePasswordVisible && (
          <ChangePassword
            show={changePasswordVisible}
            onHide={changePassword}
          />
        )}
        <Sidebar
          collapsed={collapsed}
          customBreakPoint="800px"
          onBreakPoint={setBroken}
        >
          <Menu
            menuItemStyles={{
              button: ({ level, active, disabled }) => {
                // only apply styles on first level elements of the tree
                if (level === 0)
                  return {
                    color: active ? "#ffffff" : "#6B6D7D",
                    backgroundColor: active ? "#EB0075" : undefined,
                  };
              },
            }}
          >
            <MenuItem
              className="mimologo"
              component={<Link to={homePageUrl} />}
            >
              <div onClick={handleHamburgerClick}>
                <HamburgerIcon />
              </div>
              <MimoLogo />
            </MenuItem>

            {["ADMIN", "OPERATOR", "VIEWER"].includes(role) && (
              <MenuItem
                component={<Link to={routes.dashboard} />}
                icon={<MenuIconDashboard />}
                active={location.pathname.includes(routes.dashboard)}
              >
                Dashboard
              </MenuItem>
            )}

            {["ADMIN", "OPERATOR", "VIEWER"].includes(role) && (
              <MenuItem
                component={<Link to={routes.deviceManagement} />}
                icon={<MenuIconDeviceManagement />}
                active={location.pathname.includes(routes.deviceManagement)}
              >
                Device Management
              </MenuItem>
            )}

            {["ADMIN", "OPERATOR", "VIEWER"].includes(role) && (
              <MenuItem
                component={<Link to={routes.alerts} />}
                active={alertsRoutes()}
                icon={<MenuIconAlerts />}
                className={role === "VIEWER" ? "flex-1" : ""}
              >
                Alerts
              </MenuItem>
            )}
          {['ADMIN'].includes(role) && (
            <MenuItem
              component={<Link to={routes.quickAlerts} />}
              active={location.pathname.includes(routes.quickAlerts)}
              icon={<MenuQuickAlertIcon />}
            >
              Quick Alerts
            </MenuItem>
          )}
            {["ADMIN"].includes(role) && (
              <MenuItem
                component={<Link to={routes.users} />}
                active={location.pathname.includes(routes.users)}
                icon={<MenuIconUsers />}
              >
                Users
              </MenuItem>
            )}

            {["ADMIN", "OPERATOR"].includes(role) && (
              <MenuItem
                component={<Link to={routes.deviceSetup} />}
                active={location.pathname.includes(routes.deviceSetup)}
                icon={<MenuIconDeviceSetUp />}
              >
                Device Set Up
              </MenuItem>
            )}

            {["ADMIN"].includes(role) && (
              <MenuItem
                component={<Link to={routes.integrations} />}
                active={location.pathname.includes(routes.integrations)}
                icon={<MenuIconIntegrations />}
              >
                3rd Party Integration
              </MenuItem>
            )}

            {["SUPERADMIN"].includes(role) && (
              <MenuItem
                component={<Link to={routes.analytics} />}
                active={location.pathname.includes(routes.analytics)}
                icon={<MenuIconAnalytics />}
              >
                Analytics
              </MenuItem>
            )}

            {["SUPERADMIN"].includes(role) && (
              <MenuItem
                component={<Link to={routes.superAdmin} />}
                 active={location.pathname === routes.superAdmin}
                icon={<MenuIconDashboard />}
              >
                Organizations
              </MenuItem>
            )}

            {["SUPERADMIN"].includes(role) && (
              <MenuItem
                component={<Link to={routes.superAdminDeviceManagement} />}
                active={location.pathname.includes(routes.superAdminDeviceManagement)}
                icon={<MenuIconDeviceManagement />}
              >
                Device Management
              </MenuItem>
            )}

            {["SUPERADMIN"].includes(role) && (
              <MenuItem
                component={<Link to={routes.ManageUpdates} />}
                active={location.pathname.includes(routes.ManageUpdates)}
                icon={<MenuManageFirmwareIcon />}
              >
                Manage Updates
              </MenuItem>
            )}

            {["SUPERADMIN"].includes(role) && (
              <MenuItem
                component={<Link to={routes.manageTemplates} />}
                active={location.pathname.includes(routes.manageTemplates)}
                icon={<MenuIconAlerts />}
              >
                Manage Templates
              </MenuItem>
            )}

            {["SUPERADMIN"].includes(role) && (
              <MenuItem
                component={<Link to={routes.DeviceLogs} />}
                active={location.pathname.includes(routes.DeviceLogs)}
                icon={<MenuManageFirmwareIcon />}
              >
                Device Logs
              </MenuItem>
            )}

            {["ADMIN", "OPERATOR", "SUPERADMIN"].includes(role) && (
              <MenuItem
                component={<Link to={routes.userGuide} />}
                active={location.pathname.includes(routes.userGuide)} 
                icon={<HelpIcon />} 
                className="flex-1"
              >
                Help
              </MenuItem>
            )}

            {isPasswordEnable && (
              <MenuItem
                className="logout"
                onClick={changePassword}
                icon={<ChangePasswordIcon />}
              >
                <span>Change Password</span>
              </MenuItem>
            )}
            <MenuItem className="logout" onClick={logout} icon={<LogoutIcon />}>
              <span>Logout</span>
            </MenuItem>
            {/* <MenuItem
            className='userprofile'>
            <UserProfile />
          </MenuItem> */}
          </Menu>
        </Sidebar>
      </div>
    </>
  );
};

export default SidebarComp;
